import React from 'react';
import { SlidersHorizontal, Search } from 'lucide-react';

/**
 * KioskFiltersSidebar - Simplified filter sidebar for kiosk mode
 * Shows only relevant filters for in-dealership use
 */
const KioskFiltersSidebar = ({ filters, onFilterChange, onApplyFilters, dealerName }) => {
  const bodyTypes = ['SUV', 'Sedan', 'Truck', 'EV'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    onFilterChange({
      ...filters,
      [name]: value
    });
  };

  const handleReset = () => {
    onFilterChange({
      searchTerm: '',
      type: '',
      maxMileage: '',
      priceRange: ''
    });
  };

  return (
    <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border-2 border-app-border-light dark:border-app-border p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-5 border-b-2 border-app-border-light dark:border-app-border">
        <SlidersHorizontal className="w-6 h-6 text-app-accent" />
        <h2 className="text-xl font-bold text-app-text-light dark:text-app-text">
          Filter Inventory
        </h2>
      </div>

      {/* Dealership Info */}
      {dealerName && (
        <div className="bg-app-accent/10 border border-app-accent/30 rounded-xl p-4">
          <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary mb-1">
            Showing inventory from:
          </p>
          <p className="text-lg font-bold text-app-text-light dark:text-app-text">
            {dealerName}
          </p>
        </div>
      )}

      {/* FILTERS SECTION */}
      <div className="space-y-5">
        {/* Quick Search */}
        <div>
          <label className="block text-base font-semibold text-app-text-light dark:text-app-text mb-3">
            Search
          </label>
          <div className="relative">
            <Search className="w-5 h-5 text-app-text-secondary-light dark:text-app-text-secondary absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              name="searchTerm"
              value={filters.searchTerm}
              onChange={handleChange}
              placeholder="Make or model..."
              className="w-full pl-11 pr-4 py-3 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border-2 border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent placeholder:text-app-text-secondary-light dark:placeholder:text-app-text-secondary text-base"
            />
          </div>
        </div>

        {/* Body Type */}
        <div>
          <label className="block text-base font-semibold text-app-text-light dark:text-app-text mb-3">
            Body Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onFilterChange({ ...filters, type: '' })}
              className={`py-3 px-4 rounded-xl border-2 text-sm font-semibold transition-all ${
                filters.type === ''
                  ? 'border-app-accent bg-app-accent/10 text-app-accent'
                  : 'border-app-border-light dark:border-app-border bg-app-bg-light dark:bg-app-bg text-app-text-secondary-light dark:text-app-text-secondary'
              }`}
            >
              All Types
            </button>
            {bodyTypes.map(type => (
              <button
                key={type}
                onClick={() => onFilterChange({ ...filters, type })}
                className={`py-3 px-4 rounded-xl border-2 text-sm font-semibold transition-all ${
                  filters.type === type
                    ? 'border-app-accent bg-app-accent/10 text-app-accent'
                    : 'border-app-border-light dark:border-app-border bg-app-bg-light dark:bg-app-bg text-app-text-secondary-light dark:text-app-text-secondary'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Max Mileage */}
        <div>
          <label className="block text-base font-semibold text-app-text-light dark:text-app-text mb-3">
            Maximum Mileage
          </label>
          <select
            name="maxMileage"
            value={filters.maxMileage}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border-2 border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent text-base font-medium"
          >
            <option value="">Any Mileage</option>
            <option value="25000">Under 25,000 miles</option>
            <option value="40000">Under 40,000 miles</option>
            <option value="60000">Under 60,000 miles</option>
            <option value="80000">Under 80,000 miles</option>
            <option value="100000">Under 100,000 miles</option>
          </select>
        </div>

        {/* Price Range */}
        <div>
          <label className="block text-base font-semibold text-app-text-light dark:text-app-text mb-3">
            Price Range
          </label>
          <select
            name="priceRange"
            value={filters.priceRange}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border-2 border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent text-base font-medium"
          >
            <option value="">Any Price</option>
            <option value="0-30000">Under $30,000</option>
            <option value="30000-50000">$30,000 - $50,000</option>
            <option value="50000-75000">$50,000 - $75,000</option>
            <option value="75000-100000">$75,000 - $100,000</option>
            <option value="100000+">Over $100,000</option>
          </select>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3 pt-5 border-t-2 border-app-border-light dark:border-app-border">
        <button
          type="button"
          onClick={onApplyFilters}
          className="w-full bg-app-accent hover:bg-app-accent/90 text-white py-4 rounded-xl font-bold text-lg transition-all duration-200 hover:shadow-lg"
        >
          Apply Filters
        </button>

        <button
          type="button"
          onClick={handleReset}
          className="w-full bg-app-bg-tertiary-light dark:bg-app-bg-tertiary hover:bg-app-error/10 text-app-text-light dark:text-app-text hover:text-app-error py-3 rounded-xl font-semibold text-base transition-all duration-200"
        >
          Reset All
        </button>
      </div>
    </div>
  );
};

export default KioskFiltersSidebar;
